import React, { ChangeEvent, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

interface Props {
  profileUrl?: string;
  onChange: (file?: File) => void;
}

/** --- Netflix-like palette tokens (local to this component) --- */
const NF = {
  bg: "#141414",
  panel: "#0b0b0b",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.14)",
  borderStrong: "rgba(255,255,255,0.22)",
  red: "#E50914",
  redHover: "#b20710",
  glow:
    "radial-gradient(900px 420px at 10% -10%, rgba(229,9,20,0.10), transparent 55%), radial-gradient(600px 300px at 110% 0%, rgba(255,255,255,0.04), transparent 60%)",
};

const MediaSettings: React.FC<Props> = ({ profileUrl, onChange }) => {
  const [preview, setPreview] = useState<string | undefined>(profileUrl);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  /* ——— handle file upload ——— */
  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onChange(file);
    setPreview(URL.createObjectURL(file));
  };

  /* ——— clear profile image ——— */
  const clearProfile = () => {
    setPreview(undefined);
    onChange(undefined);
  };

  /* ——— Upload Button (hidden input pattern) ——— */
  const UploadBtn = ({ label }: { label: string }) => (
    <Button
      component="label"
      variant="contained"
      startIcon={<UploadIcon />}
      sx={{
        textTransform: "none",
        fontFamily: "'Lexend', sans-serif",
        fontWeight: 800,
        letterSpacing: ".2px",
        color: NF.text,
        background: NF.red,
        borderRadius: 2,
        px: { xs: 1.75, sm: 2.25 },
        py: { xs: 0.75, sm: 1 },
        minHeight: { xs: 36, sm: 40 },
        boxShadow: "0 10px 28px rgba(229,9,20,.35)",
        "&:hover": { background: NF.redHover },
      }}
    >
      {label}
      <input hidden accept="image/*" type="file" onChange={handleFile} />
    </Button>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 3, md: 3.5 },
        borderRadius: 3,
        fontFamily: "'Lexend', sans-serif",
        color: NF.text,
        background: `${NF.glow}, ${NF.bg}`,
        border: `1px solid ${NF.border}`,
        boxShadow: "0 24px 60px rgba(0,0,0,.55)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={{ xs: 1.25, sm: 2 }}
        sx={{ mb: { xs: 2, sm: 2.5 } }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            letterSpacing: ".2px",
            textShadow: "0 1px 0 rgba(0,0,0,.35)",
            fontSize: { xs: "1.05rem", sm: "1.15rem", md: "1.25rem" },
          }}
        >
          Profile Media
        </Typography>

        <Typography
          sx={{
            color: NF.muted,
            fontSize: { xs: ".85rem", sm: ".9rem" },
          }}
        >
          Upload a clean, centered avatar. PNG/JPG recommended.
        </Typography>
      </Stack>

      {/* Content area */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2.5, sm: 3, md: 4 }}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        {/* Avatar + inline edit badge */}
        <Box
          sx={{
            display: "flex",
            justifyContent: isXs ? "center" : "flex-start",
            alignItems: "center",
            minWidth: { md: 220 },
          }}
        >
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Tooltip title="Change">
                <IconButton
                  size="small"
                  component="label"
                  sx={{
                    bgcolor: "#fff",
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    border: `1px solid ${NF.borderStrong}`,
                    "&:hover": { opacity: 0.95 },
                  }}
                >
                  <EditIcon fontSize="small" sx={{ color: "#000" }} />
                  <input hidden type="file" accept="image/*" onChange={handleFile} />
                </IconButton>
              </Tooltip>
            }
          >
            <Avatar
              src={preview || "/placeholder.jpg"}
              sx={{
                width: { xs: 96, sm: 112, md: 128, lg: 144 },
                height: { xs: 96, sm: 112, md: 128, lg: 144 },
                border: `1px solid ${NF.borderStrong}`,
                boxShadow: "0 12px 28px rgba(0,0,0,.45)",
                bgcolor: NF.panel,
              }}
            />
          </Badge>
        </Box>

        {/* Right-side controls */}
        <Stack
          spacing={{ xs: 1.5, sm: 2 }}
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: ".2px",
              fontSize: { xs: "1rem", sm: "1.05rem" },
            }}
          >
            Avatar
          </Typography>

          <Typography
            sx={{
              color: NF.muted,
              fontSize: { xs: ".9rem", sm: ".95rem" },
              lineHeight: 1.5,
            }}
          >
            Recommended: square image, good lighting, high resolution. Your avatar
            appears in sessions and across your profile.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 1.25 }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="flex-start"
            sx={{
              mt: { xs: 0.5, sm: 0.75 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <UploadBtn label={preview ? "Replace" : "Upload"} />

            {preview && (
              <Tooltip title="Remove">
                <IconButton
                  color="error"
                  onClick={clearProfile}
                  sx={{
                    width: { xs: 40, sm: 42 },
                    height: { xs: 40, sm: 42 },
                    borderRadius: 2,
                    border: `1px solid ${NF.borderStrong}`,
                    bgcolor: "rgba(255,255,255,0.06)",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.12)",
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {/* Small helper rule for ultra-small screens */}
          {!isMdUp && (
            <Box
              sx={{
                mt: 1.5,
                borderTop: `1px dashed ${NF.border}`,
                pt: 1.25,
                color: NF.muted,
                fontSize: { xs: ".85rem", sm: ".9rem" },
              }}
            >
              Tip: You can also tap the small pencil on the avatar to change it.
            </Box>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

export default MediaSettings;
